import { types, useServices } from "@/lib/openapi";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";
import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";

export type Organization = types.Organization;

export const useOrganizationsStore = defineStore("organizations", () => {
    const isLoading = ref(false);
    const organizations = ref<Organization[]>();
    const { authStatus } = useAuthenticationManager();
    const api = useServices({
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const getOrganizations = async () => {
        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;

        try {
            const { response, data } =
                await api.startOrganizationOrganizationMgtGetOrganizationsStartPost(
                    { signal: controller.value.signal }
                );

            if (response.ok) {
                organizations.value = data?.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
            } else {
                if (response.status === 0) {
                    // Request was aborted by the AbortController
                    return;
                }
                throw new Error(
                    `Request failed with status ${response.status}`
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                getOrganizations();
            }
        },
        { immediate: true }
    );

    return {
        getOrganizations,
        organizations,
        isLoading,
    };
});

export const useOrganizationsManager = () =>
    storeToRefs(useOrganizationsStore());
